import TimeStamp            from '@/core/types/TimeStamp';
import EntityPointer        from '@/domains/EntityPointer/EntityPointer';
import Comment              from '@/core/services/Comments/Comment';
import ChangelogEntry       from '@/core/services/Changelogs/ChangelogEntry';
import NotificationLogEntry from '@/core/services/Feeds/NotificationLogEntry';

/**
 * Тип записи фида. Дискриминатор для payload.
 */
export type FeedRecordType = 'changelog' | 'comment' | 'notification';

/**
 * Запись фида — зеркало FeedRecord.php.
 *
 * Полиморфный агрегат: один и тот же фид содержит записи трёх видов,
 * payload каждой гидрируется в свою доменную модель по полю type
 * (как switch в FeedRecord::fromArray). Дискриминируемый union на type
 * даёт сужение payload в потребителях без ручных проверок.
 */
class FeedRecord {

	/** Сущность-родитель записи (к чему относится строка фида). */
	parentEntity: EntityPointer;

	time: TimeStamp;

	type: FeedRecordType;

	/** Полезная нагрузка, конкретный класс зависит от type. */
	payload: ChangelogEntry | Comment | NotificationLogEntry;

	/**
	 * Транзиентное UI-состояние «запись только что появилась» — для блинка
	 * в тендерной ленте при дельта-обновлении. НЕ приходит с бека и НЕ трогается
	 * в fromServer: проставляется на фронте тем, кто умеет отличить новые записи
	 * от уже показанных (тендерный механизм по набору известных id).
	 */
	isNew: boolean = false;

	/**
	 * «Запись нашей организации» — приходит С БЕКА в публичной отдаче
	 * (feeds.getPublic), вычисляется там по текущему партнёру. Смысл — текущий
	 * ПАРТНЁР (организация), не пользователь. В приватной отдаче отсутствует → false.
	 * Управляет тем, показывать имя сотрудника (своё) или партнёра (чужое), и подсветкой.
	 */
	isMine: boolean = false;

	static fromServer(data: any, result = new FeedRecord()): FeedRecord {
		const record = result;

		record.time = TimeStamp.fromServer(data.time);
		record.type = data.type;
		record.isMine = data.isMine ?? false;

		if (data.parentEntity) {
			record.parentEntity = EntityPointer.fromServer(data.parentEntity);
		}

		if (data.type === 'changelog') {
			record.payload = ChangelogEntry.fromServer(data.payload);
		}
		else if (data.type === 'comment') {
			record.payload = Comment.fromServer(data.payload);
		}
		else if (data.type === 'notification') {
			record.payload = NotificationLogEntry.fromServer(data.payload);
		}

		return record;
	}

	get isChangelog(): boolean {
		return this.type === 'changelog';
	}

	get isComment(): boolean {
		return this.type === 'comment';
	}

	get isNotification(): boolean {
		return this.type === 'notification';
	}
}

export default FeedRecord;